<template>
  <div class="item-container" data-testid="dialogue-item">
    <!-- Drag message overlay -->
    <div v-if="isDragTarget" class="drag-overlay">
      {{ dragMessage }}
    </div>

    <!-- Drag target -->
    <div class="md-elevation-3 item-handle" aria-label="Drag Handle" data-testid="drag-handle" :class="{
      'item-handle-disabled': isDisabled,
      'item-handle-off': !item.options.enabled && (!isDisabled || state.hasFocus),
      'item-handle-on': item.options.enabled && (!isDisabled || state.hasFocus),
    }"
    >
      <span data-testid="position-label">{{ positionLabel }}.</span>
      <md-icon class="drag-icon" :class="{
        'draggable': !isDisabled || isDragTarget,
        'drag-icon-disabled': isDisabled,
        'drag-icon-off': !item.options.enabled && (!isDisabled || state.hasFocus),
      }"
      >
        drag_indicator
      </md-icon>
    </div>

    <div class="field-container">
      <!-- Question -->
      <md-field v-if="showQuestion" :md-counter="true" class="question-field" data-testid="question-field-container">
        <label data-testid="question-label">{{ itemLabel }}</label>
        <md-input
          ref="questionInput"
          :value="item.content.en"
          :class="{ error: !item.content.en, 'question-input': !settings.isAI, 'question-input-ai' : settings.isAI }"
          :disabled="readonly"
          :maxlength="256"
          aria-label="Question input"
          data-testid="question-input"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter.prevent="handleQuestionEnter"
          @input="(value) => editQuestion(value)"
        />
        <help-tooltip
          v-if="tooLongError"
          type="error"
          aria-label="Question too long error"
          data-testid="question-too-long-error"
          class="help-tooltip"
          :class="{ 'question-help-tooltip': !settings.isAI, 'question-help-tooltip-ai': settings.isAI }"
        >
          {{ tooLongError.message }}
        </help-tooltip>
        <help-tooltip
          v-if="missingQuestionError"
          type="error"
          aria-label="Missing question error"
          data-testid="missing-question-error"
          class="help-tooltip"
          :class="{ 'question-help-tooltip': !settings.isAI, 'question-help-tooltip-ai': settings.isAI }"
        >
          {{ missingQuestionError.message }}
        </help-tooltip>
        <div class="bottom-right-absolute">
          <!-- Response -->
          <md-field
            v-if="showResponse"
            class="ui-theme-3 response-field"
            data-testid="ai-response-type-container"
          >
            <label>Response Type</label>
            <md-select
              :value="item.options.response"
              aria-label="AI Response Type"
              data-testid="ai-response-type"
              @input="editResponse"
            >
              <md-option value="AI" aria-label="AI" data-testid="response-ai">AI</md-option>
              <md-option value="CUSTOM" aria-label="Custom" data-testid="response-custom">Custom</md-option>
            </md-select>
          </md-field>
          <md-checkbox
            v-if="isChild && !item.isNew"
            class="off-at-start-checkbox md-primary"
            :value="!item.options.enabled"
            aria-label="Off at start"
            data-testid="off-at-start-checkbox"
            @change="editDisabled"
          >
            Off at start
          </md-checkbox>
          <md-button
            :disabled="isDisabled"
            class="md-dense md-raised md-primary md-icon-button delete-button"
            tabindex="-1"
            aria-label="Delete dialogue"
            data-testid="delete-dialogue-button"
            @click="remove()"
          >
            <md-icon>remove</md-icon>
          </md-button>
        </div>
      </md-field>

      <!-- Answer -->
      <md-field v-if="showAnswer" :md-counter="true" data-testid="answer-field-container">
        <label>{{ item.isNew ? settings.newAnswerLabel : settings.answerLabel }}</label>
        <md-input
          ref="answerInput"
          v-model="item.options.answer.en"
          class="answer-input"
          aria-label="Answer input"
          data-testid="answer-input"
          :class="{ warning: !item.options.answer.en }"
          :disabled="readonly"
          :maxlength="256"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter.prevent="handleAnswerEnter"
          @input="(value) => editAnswer(value)"
        />
        <help-tooltip v-if="emptyUnsolicitedError" type="error" class="help-tooltip question-help-tooltip-answer">
          {{ emptyUnsolicitedError.message }}
        </help-tooltip>
        <help-tooltip
          v-else-if="tagOnlyAnswerError"
          type="error"
          class="help-tooltip question-help-tooltip-answer"
          aria-label="Tag only answer error"
          data-testid="tag-only-answer-error"
        >
          {{ tagOnlyAnswerError.message }}
        </help-tooltip>

        <div class="bottom-right-absolute">
          <help-tooltip
            v-if="!isDisabled && !item.options.answer.en && !settings.isAI"
            type="warning"
            direction="top"
            aria-label="Missing answer warning"
            data-testid="missing-answer-warning"
            class="help-tooltip"
          >
            You're missing an answer
            <br>for this question
          </help-tooltip>
          <md-button
            v-if="!isDisabled && item.options.answer.en"
            :md-ripple="false"
            class="md-icon-button md-dense play-answer-button"
            tabindex="-1"
            aria-label="Play answer"
            data-testid="play-answer-button"
            @click="playAnswerText()"
          >
            <md-icon>volume_up</md-icon>
          </md-button>

          <md-button
            v-if="settings.isQuestionDisabled"
            :disabled="isDisabled"
            class="md-dense md-raised md-primary md-icon-button delete-button"
            tabindex="-1"
            @click="remove()"
          >
            <md-icon>remove</md-icon>
          </md-button>
        </div>
      </md-field>

      <!-- Reason -->
      <md-field v-if="showReason" :md-counter="true" data-testid="reason-field-container">
        <label>{{ item.isNew ? settings.newReasonLabel : settings.reasonLabel }}</label>
        <md-input
          ref="reasonInput"
          v-model="item.options.reason.en"
          class="reason-input"
          aria-label="Reason for Response"
          data-testid="reason-input"
          :disabled="readonly"
          :maxlength="256"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter.prevent="handleReasonEnter"
          @input="(value) => editReason(value)"
        />
        <help-tooltip v-if="missingReasonError" type="error" class="help-tooltip" aria-label="Missing Reason Error" data-testid="missing-reason-error">
          {{ missingReasonError.message }}
        </help-tooltip>
      </md-field>
    </div>
  </div>
</template>

<script>
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';

export default {
  name: 'dialogue-item',
  components: {
    HelpTooltip,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    isChild: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: undefined,
    },
    warnings: {
      type: Array,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    dragMessage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      state: {
        hasFocus: false,
      }
    };
  },
  computed: {
    isDisabled() {
      return this.readonly || this.item.isNew;
    },
    isDragTarget() {
      return this.item.isNew && this.dragMessage;
    },
    itemLabel() {
      if (this.item.isNew) {
        return this.isChild ? this.settings.newChildLabel : this.settings.newQuestionLabel;
      } else {
        return this.isChild ? this.settings.childLabel : this.settings.questionLabel;
      }
    },
    reasonLabel() {
      if (this.item.isNew) {
        return 'Add a reason for this response';
      } else {
        return this.item.options.reason.en ? 'Reason' : 'Add a reason for this response';
      }
    },
    positionLabel() {
      return this.isChild ? String.fromCharCode('A'.charCodeAt(0) + this.item.options.position) : this.item.options.position + 1;
    },
    showQuestion() {
      return !this.settings.isQuestionDisabled;
    },
    showAnswer() {
      return !this.settings.isAnswerDisabled && (!this.settings.isAI || (this.settings.isAI && this.item.options.response !== 'AI'));
    },
    showResponse() {
      return this.settings.isAI && this.item.content && !this.item.isNew;
    },
    showReason() {
      return this.settings.isAI;
    },
    tooLongError() {
      return (this.errors || []).find(error => error.metadata.errorId === 'wordTooLong');
    },
    missingQuestionError() {
      return (this.errors || []).find(error => error.metadata.errorId === 'missingQuestion');
    },
    missingReasonError() {
      return (this.errors || []).find(error => error.metadata.errorId === 'missingReason');
    },
    emptyUnsolicitedError() {
      return (this.errors || []).find(error => error.metadata.errorId === 'emptyUnsolicited');
    },
    tagOnlyAnswerError() {
      return (this.errors || []).find(error => error.metadata.errorId === 'tagOnlyAnswer');
    }
  },
  created() {
    this.$eventHub.$on('DIALOGUE::ENTER', payload => {
      if (this.settings.type === payload.type && this.item.options.position === (payload.position + 1)) {
        if (this.settings.isQuestionDisabled) {
          this.focusAnswer();
        } else {
          this.focusQuestion();
        }
      }
    });
  },
  methods: {
    handleFocus() {
      this.state.hasFocus = true;
    },
    handleBlur() {
      this.state.hasFocus = false;
    },
    conditionInput(value) {
      return value.replace('\t', '    ');
    },
    editQuestion(value) {
      this.$emit('editQuestion', this.item, this.conditionInput(value));
    },
    editAnswer(value) {
      this.$emit('editAnswer', this.item, this.conditionInput(value));
    },
    editResponse(value) {
      this.$emit('editResponse', this.item, value);
    },
    editReason(value) {
      this.$emit('editReason', this.item, this.conditionInput(value));
    },
    handleQuestionEnter() {
      if (this.settings.isAnswerDisabled) {
        // Send focus to next dialogue item
        this.$eventHub.$emit('DIALOGUE::ENTER', { type: this.settings.type, position: this.item.options.position});
      } else {
        this.focusAnswer();
      }
    },
    handleAnswerEnter() {
      // Send focus to next dialogue item
      this.$eventHub.$emit('DIALOGUE::ENTER', { type: this.settings.type, position: this.item.options.position});
    },
    handleReasonEnter() {

    },
    focusQuestion() {
      if (this.$refs.questionInput) {
        this.$refs.questionInput.$el.focus();
      }
    },
    focusAnswer() {
      if (this.$refs.answerInput) {
        this.$refs.answerInput.$el.focus();
      }
    },
    playAnswerText() {
      this.$emit('playAudio', this.item);
    },
    remove() {
      this.$emit('remove', this.item);
    },
    editDisabled(value) {
      const enabled = !value; // invert for data model
      this.$emit('editEnabled', this.item, enabled);
    }
  },
}
</script>

<style scoped>
.item-container {
  position: relative;
  padding: 6px;
  padding-left: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.drag-overlay {
  /* opaque overlay to hide content */
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background-color: #f3f3f3;
  z-index: 6;
  padding: 10px;
  outline: 3px dashed lightblue;
  outline-offset: -8px;
  text-align: center;
  color: lightblue;
}

.item-handle {
  padding: 4px 6px;
  margin-left: 4px;
  font-size: 12px;
  border-radius: 3px;
  align-self: stretch;
  position: relative;
  width: 60px;
}

.item-handle-disabled {
  color: rgba(0, 0, 0, 0.54);
  background-color: #e8e8e8;
}

.item-handle-off {
  color: white;
  background-color: black;
}

.item-handle-on {
  background-color: white;
}

.item-handle-off {
  color: white !important;
}

.field-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.bottom-right-absolute {
  position: absolute;
  display: flex;
  align-items: flex-end;
  right: 4px;
  bottom: 0px;
}

.response-field {
  flex: unset;
  width: 120px;
  left: -4px;
  background-color: transparent !important;
}

.drag-icon {
  width: 50px;
  height: 50px;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -25px 0 0 -25px; /* apply negative top and left margins to truly center the element */
}

.drag-icon-disabled {
  color: rgba(0, 0, 0, 0.54) !important;
}

.drag-icon-off {
  color: white !important;
}

.draggable {
  cursor: pointer;
}

.help-tooltip {
  position: absolute;
  right: 6px;
  bottom: 6px;
}

.question-help-tooltip {
  right: 40px;
}

.question-help-tooltip-answer {
  right: 40px;
}

/* Clear both buttons when the answer row shows play and delete. */
.md-field:has(> .bottom-right-absolute > .play-answer-button):has(> .bottom-right-absolute > .delete-button) > .question-help-tooltip-answer {
  right: 68px;
}

.question-help-tooltip-ai {
  right: 160px;
}

.question-input {
  padding-right: 40px;
}

.question-input-ai {
  padding-right: 160px;
}

.answer-input {
  padding-right: 60px;
}

.md-field {
  flex: 1;
  margin: 0;
  padding: 0;
  min-height: 0;
}

.md-field >>> .md-count {
  right: 8px;
  top: 0;
}

.md-icon-button.md-dense {
    width: 24px;
    min-width: 24px;
    height: 24px;
    bottom: 6px;
}

.off-at-start-checkbox {
  margin: 0;
  margin-right: 8px;
  top: -8px;
  padding-left: 8px;
}

.off-at-start-checkbox >>> label {
  left: 0 !important;
}
</style>